import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import { formatDateTime } from '@/lib/format';
import type { ImportRow } from '@/lib/types';

const TEMPLATE_HEADERS = [
  'Nombre',
  'SKU',
  'Código de barra',
  'Descripción',
  'Categoría',
  'Marca',
  'Precio compra',
  'Precio venta',
  'Impuesto',
  'Stock',
  'Stock mínimo',
  'Mostrar catálogo',
];

function normalizeKey(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

const HEADER_MAP: Record<string, keyof ImportRow> = {
  nombre: 'name',
  sku: 'sku',
  codigodebarra: 'barcode',
  codigobarras: 'barcode',
  codigodebarras: 'barcode',
  descripcion: 'description',
  categoria: 'category_name',
  marca: 'brand',
  preciocompra: 'purchase_price',
  precioventa: 'sale_price',
  impuesto: 'tax_percentage',
  stock: 'stock',
  stockminimo: 'min_stock',
  mostrarcatalogo: 'show_in_catalog',
};

function parseBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v ?? '').trim().toLowerCase();
  return ['si', 'sí', 'yes', 'true', '1', 'x'].includes(s);
}

function toNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseFile(buffer: ArrayBuffer): ImportRow[] {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
  const rows: ImportRow[] = [];

  json.forEach((record, idx) => {
    const get = (field: keyof ImportRow) => {
      for (const [key, value] of Object.entries(record)) {
        if (HEADER_MAP[normalizeKey(key)] === field) return value;
      }
      return undefined;
    };

    rows.push({
      name: String(get('name') ?? '').trim(),
      sku: String(get('sku') ?? '').trim(),
      barcode: String(get('barcode') ?? '').trim(),
      description: String(get('description') ?? '').trim(),
      category_name: String(get('category_name') ?? '').trim(),
      brand: String(get('brand') ?? '').trim(),
      purchase_price: toNumber(get('purchase_price')),
      sale_price: toNumber(get('sale_price')) ?? 0,
      tax_percentage: toNumber(get('tax_percentage')) ?? 0,
      stock: Math.round(toNumber(get('stock')) ?? 0),
      min_stock: Math.round(toNumber(get('min_stock')) ?? 0),
      show_in_catalog: parseBool(get('show_in_catalog')),
      row: idx + 2,
    });
  });

  return rows;
}

function downloadTemplate() {
  const example = {
    Nombre: 'Coca Cola 355ml',
    SKU: 'CC-001',
    'Código de barra': '7501055300846',
    Descripción: 'Refresco de cola',
    Categoría: 'Bebidas',
    Marca: 'Coca-Cola',
    'Precio compra': 25,
    'Precio venta': 50,
    Impuesto: 18,
    Stock: 100,
    'Stock mínimo': 10,
    'Mostrar catálogo': 'Sí',
  };
  const ws = XLSX.utils.json_to_sheet([example], { header: TEMPLATE_HEADERS });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Productos');
  XLSX.writeFile(wb, 'plantilla_productos.xlsx');
}

interface HistoryRow {
  id: string;
  file_name: string | null;
  created_count: number;
  updated_count: number;
  error_count: number;
  status: string;
  user_name: string | null;
  created_at: string;
  branch?: { name: string } | null;
}

interface Classified {
  row: ImportRow;
  status: 'nuevo' | 'actualizado' | 'error';
  error?: string;
}

export default function ImportPage() {
  const { branches, activeBranchId } = useBranch();
  const [branchId, setBranchId] = useState<string>(activeBranchId ?? branches[0]?.id ?? '');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [existingSku, setExistingSku] = useState<Set<string>>(new Set());
  const [existingBarcode, setExistingBarcode] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; errors: number } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    if (branchId) return;
    setBranchId(branches[0]?.id ?? '');
  }, [branches, branchId]);

  const loadExisting = async () => {
    const { data } = await supabase.from('products').select('sku, barcode');
    const skuSet = new Set<string>();
    const barcodeSet = new Set<string>();
    for (const r of (data ?? []) as { sku: string | null; barcode: string | null }[]) {
      if (r.sku) skuSet.add(r.sku);
      if (r.barcode) barcodeSet.add(r.barcode);
    }
    setExistingSku(skuSet);
    setExistingBarcode(barcodeSet);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    const { data } = await supabase
      .from('import_history')
      .select('*, branch:branches(name)')
      .order('created_at', { ascending: false })
      .limit(20);
    setHistory((data as HistoryRow[]) ?? []);
    setHistoryLoading(false);
  };

  useEffect(() => {
    loadExisting();
    loadHistory();
  }, []);

  const classified = useMemo<Classified[]>(() => {
    const seenSku = new Map<string, number>();
    const seenBarcode = new Map<string, number>();
    return rows.map((r) => {
      if (!r.name) {
        return { row: r, status: 'error' as const, error: 'Falta el nombre' };
      }
      if (r.sale_price < 0) {
        return { row: r, status: 'error' as const, error: 'Precio inválido' };
      }
      if (r.stock < 0) {
        return { row: r, status: 'error' as const, error: 'Stock inválido' };
      }
      if (r.sku && seenSku.has(r.sku)) {
        return { row: r, status: 'error' as const, error: `SKU duplicado (fila ${seenSku.get(r.sku)})` };
      }
      if (r.barcode && seenBarcode.has(r.barcode)) {
        return { row: r, status: 'error' as const, error: `Código de barra duplicado (fila ${seenBarcode.get(r.barcode)})` };
      }
      if (r.sku) seenSku.set(r.sku, r.row);
      if (r.barcode) seenBarcode.set(r.barcode, r.row);

      const isExisting =
        (r.sku && existingSku.has(r.sku)) || (r.barcode && existingBarcode.has(r.barcode));
      return { row: r, status: isExisting ? 'actualizado' as const : 'nuevo' as const };
    });
  }, [rows, existingSku, existingBarcode]);

  const summary = useMemo(() => {
    let nuevo = 0;
    let actualizado = 0;
    let errores = 0;
    for (const c of classified) {
      if (c.status === 'nuevo') nuevo++;
      else if (c.status === 'actualizado') actualizado++;
      else errores++;
    }
    return { nuevo, actualizado, errores };
  }, [classified]);

  const handleFile = async (file: File | undefined) => {
    setResult(null);
    setParseError(null);
    if (!file) return;
    if (!branchId) {
      setParseError('Selecciona primero la sucursal destino.');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseFile(buffer);
      if (parsed.length === 0) {
        setParseError('El archivo no contiene filas de datos.');
        setRows([]);
        return;
      }
      await loadExisting();
      setRows(parsed);
      setFileName(file.name);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'No se pudo leer el archivo');
      setRows([]);
    }
  };

  const handleImport = async () => {
    if (!branchId || rows.length === 0) return;
    setImporting(true);
    setParseError(null);
    try {
      const items = rows.map((r) => ({
        name: r.name,
        sku: r.sku || null,
        barcode: r.barcode || null,
        description: r.description || null,
        category_name: r.category_name || null,
        brand: r.brand || null,
        purchase_price: r.purchase_price,
        sale_price: r.sale_price,
        tax_percentage: r.tax_percentage,
        stock: r.stock,
        min_stock: r.min_stock,
        show_in_catalog: r.show_in_catalog,
        __row: r.row,
      }));
      const { data, error } = await supabase.rpc('import_products', {
        payload: { branch_id: branchId, file_name: fileName, items },
      });
      if (error) throw error;
      setResult({
        created: data.created,
        updated: data.updated,
        errors: data.errors,
      });
      setRows([]);
      setFileName('');
      loadHistory();
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'No se pudo importar');
    } finally {
      setImporting(false);
    }
  };

  const statusBadge = (status: Classified['status']) => {
    if (status === 'nuevo')
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-900">Nuevo</span>;
    if (status === 'actualizado')
      return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Actualizado</span>;
    return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900">Error</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Importar Excel</h1>
          <p className="mt-1 text-foreground-600">Carga productos masivamente desde un archivo .xlsx.</p>
        </div>
        <button
          onClick={downloadTemplate}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-download-2-line" /> Descargar plantilla
        </button>
      </div>

      {/* Sucursal destino */}
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <label className="block text-sm font-medium text-foreground-800 mb-1.5">
          Sucursal destino
        </label>
        <select
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
          className="w-full sm:w-72 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Selecciona una sucursal</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <p className="mt-2 text-xs text-foreground-500">
          El stock importado se sumará únicamente a esta sucursal.
        </p>
      </div>

      {/* Subir archivo */}
      <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 p-8 text-center">
        <i className="ri-file-excel-2-line text-4xl text-accent-700" />
        <p className="mt-3 text-sm text-foreground-700">
          Arrastra o selecciona tu archivo <span className="font-medium">.xlsx</span>
        </p>
        <label className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer">
          <i className="ri-upload-2-line" /> Seleccionar archivo
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
        {fileName && <p className="mt-3 text-xs text-foreground-600">Archivo: {fileName}</p>}
      </div>

      {parseError && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {parseError}
        </div>
      )}

      {/* Vista previa */}
      {rows.length > 0 && (
        <div className="mt-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
              <p className="text-2xl font-heading font-bold text-accent-700">{summary.nuevo}</p>
              <p className="text-xs text-foreground-600">Nuevos</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
              <p className="text-2xl font-heading font-bold text-primary-700">{summary.actualizado}</p>
              <p className="text-xs text-foreground-600">Actualizados</p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
              <p className="text-2xl font-heading font-bold text-secondary-700">{summary.errores}</p>
              <p className="text-xs text-foreground-600">Errores</p>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-background-100 text-foreground-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Nombre</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Código</th>
                  <th className="px-4 py-3 font-medium text-right">Precio</th>
                  <th className="px-4 py-3 font-medium text-right">Stock</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {classified.map((c) => (
                  <tr key={c.row.row} className="hover:bg-background-100 transition">
                    <td className="px-4 py-2.5 text-foreground-500">{c.row.row}</td>
                    <td className="px-4 py-2.5 font-medium text-foreground-950">{c.row.name || '—'}</td>
                    <td className="px-4 py-2.5 text-foreground-700">{c.row.sku || '—'}</td>
                    <td className="px-4 py-2.5 text-foreground-700">{c.row.barcode || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-foreground-900">{c.row.sale_price}</td>
                    <td className="px-4 py-2.5 text-right text-foreground-900">{c.row.stock}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        {statusBadge(c.status)}
                        {c.error && <span className="text-xs text-accent-700">{c.error}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={() => {
                setRows([]);
                setFileName('');
                setResult(null);
              }}
              disabled={importing}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleImport}
              disabled={importing || summary.errores === classified.length}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              <i className="ri-check-double-line" />
              {importing ? 'Importando...' : 'Confirmar importación'}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          <i className="ri-checkbox-circle-line text-accent-700" /> Importación completada: {result.created}{' '}
          creados, {result.updated} actualizados, {result.errors} errores.
        </div>
      )}

      {/* Historial */}
      <div className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-foreground-950">Historial de importaciones</h2>
        {historyLoading ? (
          <div className="mt-4 flex items-center justify-center py-10">
            <div className="w-8 h-8 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <p className="mt-4 text-sm text-foreground-600">Aún no hay importaciones.</p>
        ) : (
          <div className="mt-4 rounded-lg border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-background-100 text-foreground-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Archivo</th>
                  <th className="px-4 py-3 font-medium">Sucursal</th>
                  <th className="px-4 py-3 font-medium">Usuario</th>
                  <th className="px-4 py-3 font-medium text-right">Creados</th>
                  <th className="px-4 py-3 font-medium text-right">Actualizados</th>
                  <th className="px-4 py-3 font-medium text-right">Errores</th>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {history.map((h) => (
                  <tr key={h.id} className="hover:bg-background-100 transition">
                    <td className="px-4 py-3 font-medium text-foreground-950">{h.file_name || '—'}</td>
                    <td className="px-4 py-3 text-foreground-700">{h.branch?.name || '—'}</td>
                    <td className="px-4 py-3 text-foreground-700">{h.user_name || '—'}</td>
                    <td className="px-4 py-3 text-right text-accent-700">{h.created_count}</td>
                    <td className="px-4 py-3 text-right text-primary-700">{h.updated_count}</td>
                    <td className="px-4 py-3 text-right text-secondary-700">{h.error_count}</td>
                    <td className="px-4 py-3 text-foreground-600">{formatDateTime(h.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}